/**
 * Street chatter (owner's idea): people you pass mumble to themselves, talk to each other or into a phone. Lines come from
 * content/chatter; the browser's own speech voices say them (one voice, pitch and pace per person, hashed from their id,
 * so the same walker always sounds the same) and a bubble shows the words. Client-side only — nothing goes over the wire.
 */
import type { Registry } from '../content/registry';
import type { Chatter as ChatterDef } from '../content/types';
import type { Person } from './entities';
import type { RenderState } from '../engine/render-state';
import { speech } from '../engine/speech';
import { lineId, forBrowserVoice, noteUnrendered, stationTowns } from './sprawlfm';
import { lineChoice } from './radio-choice';

export interface Bubble { text: string; until: number }
const FIRST_NAMES = ['Sam', 'Lena', 'Marco', 'Priya', 'Jonas', 'Aisha', 'Tom', 'Elif', 'Kai', 'Nora', 'Ravi', 'Mia', 'Otto', 'Zoe', 'Luis', 'Hanna'];
const HEAR = 4.5, PER_PERSON_S = 45, GAP_S: [number, number] = [2.5, 6];

export class StreetChatter {
  /** id → what they are saying, for the renderer */
  readonly bubbles = new Map<number, Bubble>();
  private spoke = new Map<number, number>();
  private nextAt = 0;
  /** reactions waiting for their speaker to appear on this client (online the walker arrives a tick after the event) */
  private pending: { who: number; on: NonNullable<ChatterDef['on']>; until: number }[] = [];
  /** 0..1 from the settings (the ambient category); 0 = bubbles only */
  level = 1;
  /** bubbles always; the browser's voices only when on (settings) */
  speech = true;
  enabled = true;
  /** plays a rendered file (main.ts wires audio.voice); a line with a file skips the browser voice */
  playFile?: (url: string, level: number) => Promise<number>;
  constructor(private reg: Registry) {}
  /** Every frame. `now` in seconds (any monotonic clock). */
  update(state: RenderState, now: number): void {
    for (const [id, b] of this.bubbles) if (b.until < now) this.bubbles.delete(id);
    if (this.pending.length) this.pending = this.pending.filter((r) => { const q = state.people.find((o) => o.id === r.who && !o.dead); if (q) { this.sayFrom(state, q, this.pickOn(r.on), now); return false; } return r.until > now; });
    if (!this.enabled || now < this.nextAt) return;
    const me = state.player;
    if (me.dead || me.inside || (me.vehicle && Math.abs(me.vehicle.speed) > 6)) return;
    // who is close enough, on foot, and has not spoken lately
    const cands: { q: Person; d: number }[] = [];
    for (const q of state.people) {
      if (q.isPlayer || q.dead || q.animal || q.vehicle || q.inside || q.role === 'police' && Math.random() < 0.7) continue;
      const d = Math.hypot(q.x - me.x, q.y - me.y); if (d > HEAR) continue;
      if (now - (this.spoke.get(q.id) ?? -1e9) < PER_PERSON_S) continue;
      cands.push({ q, d });
    }
    if (!cands.length) { this.nextAt = now + 0.5; return; }
    const { q } = cands[Math.floor(Math.random() * cands.length)];
    const other = state.people.find((o) => o !== q && !o.isPlayer && !o.dead && !o.animal && !o.vehicle && Math.hypot(o.x - q.x, o.y - q.y) < 2.5);
    const def = this.pick(state, q, !!other);
    if (!def) { this.nextAt = now + 1; return; }
    this.sayFrom(state, q, def, now);
    this.nextAt = now + GAP_S[0] + Math.random() * (GAP_S[1] - GAP_S[0]);
    if (this.spoke.size > 400) for (const [id, t] of this.spoke) if (now - t > PER_PERSON_S) this.spoke.delete(id);
  }
  /** (Oct 2, owner: "add some new lines for npcs to say in various situations") something happened near (x, y): the nearest walker within
   *  `r` who has not just spoken says a line for it — a gunshot, an explosion, a crash, a horn. One reaction of a kind every few seconds
   *  (a burst of fire is one gasp, not twenty). `notId`: the person who did it (the shooter does not gasp at their own shot) */
  witness(state: RenderState, x: number, y: number, r: number, on: NonNullable<ChatterDef['on']>, now: number, notId?: number): void {
    if (!this.enabled || now < (this.kindAt.get(on) ?? 0)) return;
    let best: Person | undefined, bd = r;
    for (const q of state.people) {
      if (q.isPlayer || q.dead || q.animal || q.vehicle || q.inside || q.id === notId) continue;
      const d = Math.hypot(q.x - x, q.y - y); if (d >= bd || now - (this.spoke.get(q.id) ?? -1e9) < 6) continue;
      bd = d; best = q;
    }
    if (!best) return;
    const def = this.pickOn(on); if (!def) return;
    this.kindAt.set(on, now + 5); this.sayFrom(state, best, def, now);
  }
  private kindAt = new Map<string, number>();
  private situAt = 0;
  /** (Oct 2) what the street notices about you, looked at twice a second: a gun pointed at someone, your stars, your wounds, your dancing,
   *  a police siren going by, you driving past fast — each said by a walker near it, now and then (`witness`'s pace) */
  situations(state: RenderState, now: number): void {
    if (!this.enabled || now < this.situAt) return;
    this.situAt = now + 0.5;
    const me = state.player; if (me.dead || me.inside) return;
    const near = (r: number, x = me.x, y = me.y): Person | undefined => { let b: Person | undefined, bd = r; for (const q of state.people) { if (q.isPlayer || q.dead || q.animal || q.vehicle || q.inside) continue; const d = Math.hypot(q.x - x, q.y - y); if (d < bd) { bd = d; b = q; } } return b; };
    if (!me.vehicle) {
      const w = me.weapons[me.weaponIndex];
      if (w && w.def.class !== 'melee' && w.def.class !== 'thrown') {
        // the walker the gun points at: within 10 m and 12° of the aim
        const fx = Math.sin(me.angle), fy = -Math.cos(me.angle);
        for (const q of state.people) {
          if (q.isPlayer || q.dead || q.animal || q.vehicle || q.inside) continue;
          const dx = q.x - me.x, dy = q.y - me.y, d = Math.hypot(dx, dy); if (d > 10 || d < 0.8) continue;
          if ((dx * fx + dy * fy) / d > Math.cos(0.21)) { this.witness(state, q.x, q.y, 0.5, 'aimed_at', now); break; }
        }
      }
      if (me.hp < me.maxHp * 0.35) this.witness(state, me.x, me.y, 5, 'hurt', now);
      if (me.act === 'dance') this.witness(state, me.x, me.y, 8, 'dancing', now);
    } else if (me.vehicle.driver === me && Math.abs(me.vehicle.speed) > 20) {
      const q = near(4, me.vehicle.x, me.vehicle.y); if (q) this.witness(state, q.x, q.y, 0.5, 'near_miss', now);
    }
    if ((state.wanted ?? 0) >= 2) this.witness(state, me.x, me.y, 7, 'wanted', now);
    for (const v of state.vehicles) if (v.siren && Math.abs(v.speed) > 5 && Math.hypot(v.x - me.x, v.y - me.y) < 30) { this.witness(state, v.x, v.y, 15, 'siren', now); break; }
  }
  /** Something happened to a person (a carjack: the driver you pulled out) — they say a line for it, in their own voice (owner: 'this can't be happening', 'I'm calling the cops'). */
  react(state: RenderState, who: number, on: NonNullable<ChatterDef['on']>, now: number): void {
    if (!this.enabled) return;
    const q = state.people.find((o) => o.id === who && !o.dead);
    if (q) this.sayFrom(state, q, this.pickOn(on), now); else this.pending.push({ who, on, until: now + 2 });
  }
  private pickOn(on: NonNullable<ChatterDef['on']>): ChatterDef | undefined {
    const list = this.reg.all('chatter').filter((c) => c.on === on); if (!list.length) return undefined;
    let total = 0; for (const c of list) total += c.weight ?? 1; let r = Math.random() * total;
    for (const c of list) { r -= c.weight ?? 1; if (r <= 0) return c; } return list[list.length - 1];
  }
  /** one line of `def` from `q`: the bubble, the rendered file or the browser voice */
  private sayFrom(state: RenderState, q: Person, def: ChatterDef | undefined, now: number): void {
    if (!def) return;
    const me = state.player;
    const raw = def.lines[Math.floor(Math.random() * def.lines.length)]
      .replace(/\{name\}/g, FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)]);
    // the walker's own town in the words; out past the towns the export renders (the ones around the origin) that line has no
    // file and the browser voice would read it out for ever, so a rendered town stands in instead (owner, Sep 16)
    const here = state.world.gen.fields.settlements.in(q.x, q.y)?.name ?? 'Main';
    const url = (text: string) => state.radioAudioUrl?.(lineId(def.id, text));
    let line = raw.replace(/\{town\}/g, here);
    if (raw.includes('{town}') && !url(line)) {
      const towns = this.towns(state);
      for (let i = 0; i < towns.length; i++) { const t = towns[(q.id + i) % towns.length]; const v = raw.replace(/\{town\}/g, t); if (url(v)) { line = v; break; } }
    }
    // a rendered file for this line (the owner's TTS via the Voices page — keyed like the station's lines) and its edited words, if any
    const id = lineId(def.id, line); const fileUrl = url(line); const said = state.radioLine?.(id) ?? line;
    const shown = forBrowserVoice(said); // ([sigh] and friends are for a real TTS; the bubble and the browser voice get the words)
    const text = def.mode === 'phone' ? `📞 ${shown}` : shown;
    this.bubbles.set(q.id, { text, until: now + Math.min(5, 1.6 + line.length * 0.06) });
    this.spoke.set(q.id, now);
    const level = Math.max(0.25, 1 - Math.hypot(q.x - me.x, q.y - me.y) / (HEAR + 1));
    if (fileUrl && this.playFile && this.speech && this.level > 0) void this.playFile(fileUrl, level * this.level);
    // (owner, Sep 25 — radio-choice.ts) no file: the browser's voice only on a server with no rendered lines at all (or alone); else the bubble says it
    else { if (!fileUrl) noteUnrendered(def.id, id, shown); if (lineChoice(!!fileUrl, state.rendered ? state.rendered().lines : false, state.rendered?.().fallback) === 'fallback') this.speak(q, shown, level); }
  }
  /** the towns the export fills {town} with (the settlements around the origin, sorted — sprawlfm.stationTowns), once per seed */
  private townList?: { seed: number; towns: string[] };
  private towns(state: RenderState): string[] {
    if (this.townList?.seed !== state.seed) { const s = state.world.gen.fields.settlements; const t = stationTowns((i, j) => s.at(i, j)); this.townList = { seed: state.seed, towns: t.length ? t : ['Main'] }; }
    return this.townList.towns;
  }
  private pick(state: RenderState, q: Person, hasOther: boolean): ChatterDef | undefined {
    const hour = state.hour, night = hour >= 21 || hour < 5;
    const zone = state.world.gen.fields.urbanZone(q.x, q.y);
    const k = state.stats().karma ?? 0, dMe = Math.hypot(q.x - state.player.x, q.y - state.player.y);
    const wet = (state.world.wet ?? 0) > 0.25; // (Oct 2: lines for the rain, lines for the dry)
    const list = this.reg.all('chatter').filter((c) => !c.on && (!c.night || night) && (!c.weather || (c.weather === 'rain') === wet) && (!c.zones || c.zones.includes(zone)) && (c.mode !== 'other' || hasOther) && k >= (c.karma?.min ?? -100) && k <= (c.karma?.max ?? 100) && (!c.near || dMe <= c.near));
    let total = 0; for (const c of list) total += c.weight ?? 1;
    let r = Math.random() * total;
    for (const c of list) { r -= c.weight ?? 1; if (r <= 0) return c; }
    return list[list.length - 1];
  }
  /** The browser's voices (engine/speech.ts): the same person always gets the same voice, pitch and pace. */
  private speak(q: Person, text: string, level: number): void {
    if (!this.speech || this.level <= 0) return;
    const h = ((q.id * 2654435761) >>> 0), h1 = (h % 1000) / 1000, h2 = ((h >>> 10) % 1000) / 1000;
    speech.say(text, { sex: h % 2 ? 'f' : 'm', pitch: 0.75 + h1 * 0.6, rate: 0.9 + h2 * 0.28, seed: h >>> 3 }, level * this.level);
  }
}
